'use client';

import { useRouter } from '@peerless/ui-locale';
import { useEffect } from 'react';

const INTERCEPT_SYMBOL = Symbol('intercepted');

declare global {
  interface Window {
    [INTERCEPT_SYMBOL]: boolean;
  }
}

export interface FinderWindowInterceptProps {
  urlPrefix: string;
  queryString: Record<string, string> | undefined;
}

export function FinderWindowIntercept({ urlPrefix, queryString }: FinderWindowInterceptProps) {
  const router = useRouter();

  useEffect(() => {
    return interceptWindowOpen(router, urlPrefix, queryString);
  }, [router, urlPrefix, queryString]);

  return null;
}

function interceptWindowOpen(
  router: ReturnType<typeof useRouter>,
  urlPrefix: string,
  queryString: Record<string, string> | undefined
) {
  if (!window[INTERCEPT_SYMBOL]) {
    const inner = window.open;

    window.open = (function (open) {
      return function (url, name, features) {
        if (typeof url === 'string' && url.startsWith(urlPrefix)) {
          url = rewriteUrl(url, queryString);

          // use the router to navigate to the new URL
          router.push(url);
          return null; // prevent the default window.open behavior
        }

        return open.call(window, url, name, features);
      };
    })(inner);

    window[INTERCEPT_SYMBOL] = true;

    // return a teardown function
    return () => {
      window.open = inner;
      window[INTERCEPT_SYMBOL] = false;
    };
  }

  return () => {
    // no-op as window open is already intercepted
  };
}

function rewriteUrl(url: string, queryString: Record<string, string> | undefined): string {
  if (queryString === undefined) {
    return url;
  }

  const qs = new URLSearchParams(queryString);

  if (url.includes('?')) {
    return `${url}&${qs.toString()}`;
  } else {
    return `${url}?${qs.toString()}`;
  }
}
